import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetAccessUsersQuery } from "../api/Endpoints/Core/Security/Users";
import { getCurrentUser, hasAccess } from "../helpers/session_helper";

// ─── Per-system menu slices ───────────────────────────────────────────────────
import { coreMenuItems } from "./Components/CoreMenuItems";
import { globoMenuItems } from "./Components/GloboMenuItems";

/**
 * ─── SYSTEM REGISTRY ──────────────────────────────────────────────────────────
 * To turn a system ON/OFF: just flip `enabled`. Nothing else to touch.
 *
 * Fields:
 *   key       – unique id, also the `iscurrentState` value & startsWith() prefix
 *   enabled   – true = shown, false = hidden (still safe to leave config here)
 *   subKeys   – inner accordion sub-group keys (for build* menus). [] for static menus.
 *   build     – (toggles, setIscurrentState) => menuItems[]. Receives a `t(key)`
 *               helper returning { [key]: bool, [setKey]: fn } style pairs.
 *   static    – a ready-made menuItems array (mutually exclusive with `build`).
 *   order     – ascending sort position within the Systems section.
 */
const SYSTEM_REGISTRY = [
  {
    key: "Globo", enabled: true, order: 80, subKeys: [],
    static: globoMenuItems,
  },
];

// Active systems, sorted. This is the only derived list the render path uses.
const ACTIVE_SYSTEMS = SYSTEM_REGISTRY
  .filter((s) => s.enabled)
  .sort((a, b) => a.order - b.order);

// Every collapse-state key the UI needs: outer key + "<key><subKey>" for each sub.
// Also includes the "Priv" special-case naturally since its key is already "HRISPriv".
const ALL_STATE_KEYS = ACTIVE_SYSTEMS.flatMap((s) => [
  s.key,
  ...s.subKeys.map((sub) => `${s.key}${sub}`),
]);

const Navdata = () => {
  const history = useNavigate();
  const location = useLocation();

  const [iscurrentState, setIscurrentState] = useState("Dashboard");

  const shouldFetch = getCurrentUser();
  const { data, refetch } = useGetAccessUsersQuery({}, { skip: !shouldFetch });
  const accessRights = data?.returnData?.map(({ permissionTypeId, allowRead, allowWrite, allowDelete }) => ({
    permissionTypeId, allowRead, allowWrite, allowDelete,
  }));

  useEffect(() => { refetch(); }, [location.pathname]);
  useEffect(() => {
    if (accessRights) sessionStorage.setItem("accessRights", JSON.stringify(accessRights));
  }, [accessRights]);

  // ── Static (non-system) top-level collapse states ───────────────────────
  const [isApps, setIsApps] = useState(false);
  const [isAuth, setIsAuth] = useState(false);
  const [isPages, setIsPages] = useState(false);
  const [isDashboard, setIsDashboard] = useState(false);

  // ── One collapse-state bag for ALL system accordions & sub-accordions ───
  // Keyed by state key (e.g. "FMS", "FMSAccounting"). Single object + setter
  // means adding a system needs no new useState lines.
  const [collapse, setCollapse] = useState(() =>
    Object.fromEntries(ALL_STATE_KEYS.map((k) => [k, false]))
  );
  const setCollapseKey = (key, value) =>
    setCollapse((prev) => ({ ...prev, [key]: value }));

  // t(key) → { v: currentBool, set: fn(bool) } — the accessor passed to build*()
  const makeToggleAccessor = () => (key) => ({
    v: collapse[key] || false,
    set: (value) => setCollapseKey(key, value),
  });

  // ── Auto-collapse on section change ─────────────────────────────────────
  useEffect(() => {
    document.body.classList.remove("twocolumn-panel");

    // Static sections.
    if (iscurrentState !== "Apps") setIsApps(false);
    if (iscurrentState !== "Auth") setIsAuth(false);
    if (iscurrentState !== "Pages") setIsPages(false);
    if (iscurrentState !== "Dashboard") setIsDashboard(false);

    // System sections: collapse any whose prefix isn't the current state.
    // Special-case HRIS vs HRISPriv so leaving one doesn't wrongly keep the other.
    setCollapse((prev) => {
      const next = { ...prev };
      for (const sys of ACTIVE_SYSTEMS) {
        let leaving = !iscurrentState.startsWith(sys.key);

        // "HRIS" must not treat "HRISPriv*" as inside it, and vice-versa.
        if (sys.key === "HRIS" && iscurrentState.startsWith("HRISPriv")) leaving = true;
        if (sys.key === "HRISPriv" && !iscurrentState.startsWith("HRISPriv")) leaving = true;

        if (leaving) {
          next[sys.key] = false;
          for (const sub of sys.subKeys) next[`${sys.key}${sub}`] = false;
        }
      }
      return next;
    });
  }, [history, iscurrentState]);

  // ── withToggle — wires outer accordion state onto top-level menu items ──
  function withToggle(items, stateVar, setter, stateKey) {
    return items.map(function (item) {
      if (!item.subItems) return item;
      return Object.assign({}, item, {
        stateVariables: stateVar,
        click: function (e) {
          e.preventDefault();
          setter(!stateVar);
          setIscurrentState(stateKey);
        },
      });
    });
  }

  // ── Build every active system's menu items from the registry ────────────
  const systemMenuItems = useMemo(() => {
    const t = makeToggleAccessor();
    return ACTIVE_SYSTEMS.flatMap((sys) => {
      const items = sys.static ?? sys.build(t, setIscurrentState);
      const outerState = collapse[sys.key] || false;
      const setter = (value) => setCollapseKey(sys.key, value);
      return withToggle(items, outerState, setter, sys.key);
    });
    // collapse is the only reactive dep; setIscurrentState is stable.
  }, [collapse]);

  // ── Assemble full menu ──────────────────────────────────────────────────
  const SYSTEMS_HEADER_IDX = 1;
  const beforeSystems = coreMenuItems.slice(0, SYSTEMS_HEADER_IDX + 1);
  const afterSystems = coreMenuItems.slice(SYSTEMS_HEADER_IDX + 1);

  const menuItems = [
    ...beforeSystems,
    ...systemMenuItems,
    ...afterSystems,
  ];

  // ── Permission filter ───────────────────────────────────────────────────
  const filterMenu = (items) => {
    return items
      .filter(item => hasAccess(item.permissionTypeId, accessRights))
      .map(item => {
        const newItem = { ...item };
        if (item.subItems) {
          newItem.subItems = filterMenu(item.subItems);
          if (newItem.subItems.length === 0) return null;
        }
        if (item.childItems) {
          newItem.childItems = filterMenu(item.childItems);
          if (newItem.childItems.length === 0) return null;
        }
        return newItem;
      })
      .filter(Boolean);
  };

  return <React.Fragment>{filterMenu(menuItems)}</React.Fragment>;
};

export default Navdata;